import { createSlice } from '@reduxjs/toolkit';
import { GlobalState, Thunk } from '../../../types.global';
import { fetchTiles } from './thunks';
import { AdTile, Tile, TilesState } from './types';
import { formatAdTile } from './utils';
import { browserStorageUpdate } from '../../../store/actions';
import { getOhCookie } from '../../../../shared/cookie';
import { addShells } from '../../../../shared/redis';

const DEFAULT_GAMES = {
  title: 'Play for Free',
  type: 'games',
  id: Date.now(),
} as const;

export const getInitialTilesState = (state: GlobalState): TilesState => {
  const { tiles, user } = state;

  const newGamesPromo = (() => {
    const { shellsCounter } = user.shells || { shellsCounter: 0 };
    const ntpVisitCounter = user.ntpVisitCounter + 1;

    const gamesPromo = tiles?.gamesPromo?.version ? tiles.gamesPromo : {};

    const data = {
      version: 1,
      enabledCounter: 0,
      gamesClicked: false,
      shownAtNtpVisitCounter: null,
      shownAtShellsCounter: null,
      closedAt: null,
      ...gamesPromo,
    };

    // if no ab test or clicked games never show promo
    if (data.gamesClicked) return data;

    const timesDisplayed =
      ntpVisitCounter - (data.shownAtNtpVisitCounter || ntpVisitCounter);

    if (!data.closedAt && timesDisplayed >= 3) {
      return {
        ...data,
        // it should actually be a time of opening last tab
        closedAt: Date.now(),
      };
    }

    const shellDifference = shellsCounter - (data.shownAtShellsCounter || 0);

    if (
      (data.enabledCounter === 0 && shellDifference >= 350) ||
      (data.enabledCounter === 1 && shellDifference >= 1200)
    ) {
      return {
        ...data,
        enabledCounter: data.enabledCounter + 1,
        shownAtNtpVisitCounter: ntpVisitCounter,
        shownAtShellsCounter: shellsCounter,
        closedAt: null,
      };
    }

    return data;
  })();

  let restoredAt = tiles?.adTiles.restoredAt || user.installationDate;
  const [valueMap, gamesValue]: any = (() => {
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;

    if (restoredAt && now - restoredAt < DAY * 90) {
      return [
        [...tiles.adTiles.valueMap, true].slice(0, 5),
        tiles.games ? tiles.games?.value : DEFAULT_GAMES,
      ];
    }

    restoredAt = now;
    // set default or reset after 90 days
    return [[true, true, true, true, true], DEFAULT_GAMES];
  })();

  return {
    ...tiles,
    adTiles: {
      value: [],
      savedAt: 0,
      clickedCount: 0,
      clickTracking: {},
      ...(tiles ? tiles.adTiles : {}),
      valueMap,
      restoredAt,

      removedUrls: tiles?.adTiles?.removedUrls || [],
    },
    games: {
      value: gamesValue,
    },
    gamesPromo: newGamesPromo,
    shortcuts: { value: [], ...(tiles ? tiles.shortcuts : {}) },
    topSites: { value: [], ...(tiles ? tiles.topSites : {}) },
  };
};

const removeParamFromUrl = (url, param) => {
  const urlObj = new URL(url);
  urlObj.searchParams.delete(param);
  return urlObj.toString();
}

export const tilesSlice = createSlice({
  name: 'tiles',
  initialState: null,
  reducers: {
    clickedAd: (state, { payload }): void => {
      const { adUrl } = payload;
      const baseUrl = removeParamFromUrl(adUrl, "index");

      state.adTiles.clickTracking = state.adTiles.clickTracking || {};

      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;

      const adClicks = state.adTiles.clickTracking[baseUrl] || {
        count: 0,
        lastClickAt: null,
        hiddenUntil: null
      };

      if (adClicks.lastClickAt && now - adClicks.lastClickAt > oneDay && adClicks.count < 3) {
        adClicks.count = 0;
        adClicks.lastClickAt = null;
        adClicks.hiddenUntil = null;
      }

      if (!adClicks.hiddenUntil || now > adClicks.hiddenUntil) {
        adClicks.count++;
        if (!adClicks.lastClickAt) {
          adClicks.lastClickAt = now;
        }

        if (adClicks.count >= 3) {
          adClicks.hiddenUntil = now + oneDay;
        }
      }

      state.adTiles.clickTracking[baseUrl] = adClicks;

      state.adTiles.clickedCount++;
      // state.onboarding.lastTopSitesViewed = Date.now();
    },
    addShortcut: (state, { payload }): void => {
      state.shortcuts.value = [
        { ...payload, type: 'shortcuts', id: Date.now() },
        ...state.shortcuts.value,
      ];
    },
    editTile: (state, { payload }): void => {
      state[payload.type].value = state[payload.type].value.map((tile) =>
        tile.id === payload.id ? payload : tile
      );
    },
    stopPromotingGames: (state, { payload }): void => {
      state.gamesPromo.closedAt = payload.now;
      state.gamesPromo.gamesClicked =
        payload.gamesClicked || state.gamesPromo.gamesClicked;
    },
    removeTile: (state, { payload }): void => {
      const { type, id } = payload.tile;
      ({
        adTiles: (): void => {
          const tileIndex = state.adTiles.value.findIndex(
            (tile) => tile.id === id
          );
          const providedIndex = payload.index;

          if (tileIndex === -1) {
            let trueCounter = 0;
            state.adTiles.valueMap = state.adTiles.valueMap.map((v) => {
              if (v) trueCounter++;
              return trueCounter - 1 === providedIndex ? false : v;
            });

            // state.adTiles.valueMap[payload.index] = false;
          } else {
            state.adTiles.valueMap[tileIndex] = false;
          }

          state.adTiles.removedUrls = [
            ...(state.adTiles.removedUrls || []),
            id,
          ];
        },
        shortcuts: (): void => {
          state.shortcuts.value = state.shortcuts.value.filter(
            (tile: Tile) => tile.id !== id
          );
        },
        games: (): void => {
          state.games.value = null;
        },
        topSites: (): void => {
          state.topSites.value = state.topSites.value.map((tile) =>
            tile.id === id ? { ...tile, removed: true } : tile
          );
        },
      }[type]());
    },
  },
  extraReducers: (builder) => {
    builder.addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialTilesState(payload.state);
      }
    });

    builder.addCase(fetchTiles.fulfilled, (state: TilesState, { payload }) => {
      if (payload.topSites) {
        state.topSites.savedAt = payload.topSites.savedAt;
        state.topSites.value = (payload.topSites.value || []).map(
          (tile) =>
            state.topSites.value.find(({ id }) => id === tile.url) || {
              ...tile,
              type: 'topSites',
              id: tile.url,
            }
        );
      }

      if (payload.adTiles && Array.isArray(payload.adTiles.value)) {
        state.adTiles.value = payload.adTiles.value.map(formatAdTile);
        state.adTiles.savedAt = payload.adTiles.savedAt;
      }
    });
  },
});

export const { removeTile, editTile, addShortcut, stopPromotingGames } =
  tilesSlice.actions;

export const impressionAd =
  (ad: AdTile): Thunk =>
    async (dispatch) => {
      dispatch(tilesSlice.actions.clickedAd({ adUrl: ad.click_url }));
    };

export const clickedAd =
  (ad: AdTile): Thunk =>
    async (dispatch) => {
      const click_id = await getOhCookie('click_id');
      const params = [
        ad?.title || ad?.name,
        ad?.provider || 'no-provider'
      ];

      // Send a postback
      try {
        await Promise.all(
          params.map(f =>
            fetch(`https://p.oceanhero.today/?click_id=${click_id}&p=p8&f=${f}`)
          )
        );
      } catch (err) {
        console.error(err);
      }

      // Clicked_on_an_ad_tile_on_ntp
      addShells(50);

      dispatch(tilesSlice.actions.clickedAd({ adUrl: ad.click_url }));
    };

clickedAd.toString = (): string => tilesSlice.actions.clickedAd.toString();

export default tilesSlice.reducer;
